<svelte:options accessors={true} immutable={true} />

<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { initNavState, initLoggedIn, initCartCount, initCurrentUrl, setFlags } from '../../js/stores';
  import { getUrl } from '../../js/urlUtils';
  import Header from './Header/Header.svelte';
  import SquareSans from './SquareSans/SquareSans.svelte';
  import { DESKTOP_BREAKPOINT } from '../../js/constants';

  export let dataJSON = undefined;
  export let imageSubnavJSON = undefined;
  export let ctaSubnavJSON = undefined;
  export let flagsJSON = undefined;
  export let loggedIn = undefined;

  export let darkMode = false;
  export let themeColor = '#006aff';
  export let themeFocusColor = '#0055cc';
  export let zIndex = 1000;
  export let cartCount = 0;
  export let currentUrl = getUrl();

  const navStateStore = initNavState();
  const loggedInStore = initLoggedIn(loggedIn);
  const cartCountStore = initCartCount(cartCount);
  const currentUrlStore = initCurrentUrl(currentUrl);
  const dispatch = createEventDispatcher();

  // width bound to window's innerWidth below
  let innerWidth;

  // width bound to nav width below
  let clientWidth;

  $: desktopMode = innerWidth === undefined ? null : innerWidth >= DESKTOP_BREAKPOINT;
  $: mobileMode = desktopMode === false;

  // When props change, update the stores
  $: $loggedInStore = loggedIn;
  $: $cartCountStore = cartCount;
  $: $currentUrlStore = currentUrl;

  // When desktopMode changes, reset the navState
  $: $navStateStore = desktopMode ? {} : {};

  $: data = dataJSON ? JSON.parse(dataJSON) : {};

  $: imageSubnavData = imageSubnavJSON ? JSON.parse(imageSubnavJSON) : undefined;

  $: ctaSubnavData = ctaSubnavJSON ? JSON.parse(ctaSubnavJSON) : undefined;

  $: if (data.cart && data.cart.dataUrl) {
    if (typeof window !== 'undefined') {
      fetchCartData();
    }
  }

  $: if (loggedIn === undefined) {
    if (typeof window !== 'undefined') {
      fetchLoggedInState();
    }
  }

  // fetchCartData() returns the fetch promise
  // so it can be chained from outside the component
  export function fetchCartData() {
    return fetch(data.cart.dataUrl)
      .then((response) => response.json())
      .then((data) => {
        cartCount = data.item_count;
      })
      .catch((error) => {
        console.error('PublicWebNav: Unable to fetch cart data', error);
      });
  }

  // fetchLoggedInState() returns the fetch promise
  // so it can be chained from outside the component
  export function fetchLoggedInState() {
    return fetch('/mp/status')
      .then((response) => {
        if (response.status === 200) {
          loggedIn = true;
        }
        // default loggedIn is false so we don't need an else case here
        return response.json();
      })
      .catch((error) => {
        console.error('PublicWebNav: Unable to fetch logged in state', error);
      });
  }

  onMount(() => {
    // Initialize feature flag store
    setFlags(flagsJSON);

    dispatch('mount', { meta: data && data.meta });
  });
</script>

<SquareSans />

<svelte:window bind:innerWidth />

<div
  class="PublicWebNav"
  class:darkMode
  data-nav-monorepo
  data-nav-device-type={desktopMode ? 'Desktop' : 'Mobile'}
  bind:clientWidth
  style="--color-theme: {themeColor}; --color-theme-focus: {themeFocusColor}; z-index: {zIndex}; --desktop-breakpoint:{DESKTOP_BREAKPOINT}px; --nav-width:{clientWidth}px;"
>
  <Header {data} {imageSubnavData} {ctaSubnavData} {desktopMode} {mobileMode} />
</div>

<style>.PublicWebNav {
  position: relative;
  --color-theme: #006aff;
  --color-theme-focus: #05c;
  --color-text: #1a1a1a;
  --color-background: #fff;
  --color-background-rgb: 255, 255, 255;
  --color-border: #d9d9d9;
  --color-highlight: rgba(26, 26, 26, 0.1);
  --color-gray: #737373;
}
.PublicWebNav.darkMode {
  --color-text: #fff;
  --color-background: #030303;
  --color-background-rgb: 0, 0, 0;
  --color-border: #333;
  --color-highlight: rgba(255, 255, 255, 0.1);
  --color-gray: #b3b3b3;
}</style>
